import { clsx } from "clsx";

import { useControlledState } from "@react-stately/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { useTranslations } from "translations";

export type Step = {
  ["aria-label"]?: string;
  /**
   * For displaying label next to step
   */
  label?: string;
};

const STEP_CIRCLE_CLASSES = {
  active: "bg-transparent border-primary text-primary",
  complete: "bg-primary border-primary text-white",
  inactive: "bg-gray-200 border-gray-200 text-black",
};

const LABEL_CLASSES = {
  active: "text-black",
  complete: "text-black",
  inactive: "text-gray-600",
};

const LINE_CLASSES = {
  complete: "bg-primary",
  default: "bg-gray-300",
};

export type StepperProps<T extends number> = {
  className?: string;
  currentStep?: T;
  initialStep?: T;
  navAriaLabel?: string;
  onStepChange?: (stepIndex: T) => void;
  stepClassName?: string;
  steps: Record<T, Step>;
};

const getStepArray = <T extends number>(steps: Record<T, Step>) =>
  Object.entries(steps).map(([positionString, step]) => ({
    step,
    stepPosition: Number(positionString) as T,
  })) as { step: Step; stepPosition: T }[];

export function Stepper<T extends number>({
  className,
  currentStep: currentStepProp,
  initialStep,
  navAriaLabel,
  onStepChange,
  stepClassName,
  steps = [],
}: StepperProps<T>) {
  const translations = useTranslations();
  const stepArray = getStepArray(steps);

  const [currentStep, setCurrentStep] = useControlledState<T>(
    currentStepProp,
    initialStep ?? (stepArray?.[0]?.stepPosition || (0 as T)),
    onStepChange,
  );

  if (!stepArray.length) {
    return null;
  }

  const hasOnStepChange = typeof onStepChange === "function";

  return (
    <LazyMotion features={domAnimation}>
      <nav
        aria-label={navAriaLabel}
        className="-my-4 max-w-fit overflow-x-auto px-2 py-4"
      >
        <div
          aria-label={translations.accessibility.stepperStepOfAriaLabel({
            currentStep: (currentStep + 1).toString(),
            totalSteps: stepArray.length.toString(),
          })}
          aria-live="polite"
          aria-valuemax={stepArray.length}
          aria-valuemin={1}
          aria-valuenow={currentStep + 1}
          role="progressbar"
        >
          <ol className={clsx("flex flex-row flex-nowrap gap-x-3", className)}>
            {stepArray.map(({ step, stepPosition }, index) => {
              const status =
                currentStep === stepPosition
                  ? "active"
                  : currentStep < stepPosition
                  ? "inactive"
                  : "complete";

              const stepContent = (
                <>
                  <div className="relative flex items-center">
                    <m.div animate={status} className="relative">
                      <m.div
                        className={clsx(
                          "relative flex h-[30px] w-[30px] items-center justify-center rounded-full border-medium text-body-md font-semibold text-default-foreground",
                          STEP_CIRCLE_CLASSES[status],
                          {
                            "font-bold": status === "active",
                            "font-medium": status !== "active",
                          },
                        )}
                        initial={false}
                        transition={{ duration: 0.25 }}
                      >
                        <div
                          className={clsx(
                            "flex items-center justify-center",
                            STEP_CIRCLE_CLASSES[status],
                            {
                              "font-bold": status === "active",
                              "font-medium": status !== "active",
                            },
                          )}
                        >
                          <span>{stepPosition + 1}</span>
                        </div>
                      </m.div>
                    </m.div>
                  </div>
                  {step.label && (
                    <div className="max-w-full flex-1 text-start">
                      <div
                        className={clsx(
                          "lg:text-medium text-small transition-[color,opacity] duration-300 group-active:opacity-80",
                          LABEL_CLASSES[status],
                          {
                            "font-bold": status === "active",
                            "font-medium": status !== "active",
                          },
                        )}
                      >
                        {step.label}
                      </div>
                    </div>
                  )}
                  {stepPosition < stepArray.length - 1 && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute right-0 w-10 flex-none items-center"
                    >
                      <div
                        className={clsx(
                          "relative h-0.5 w-full transition-colors duration-300",
                          currentStep > stepPosition
                            ? LINE_CLASSES.complete
                            : LINE_CLASSES.default,
                        )}
                      />
                    </div>
                  )}
                </>
              );

              return (
                <li
                  key={stepPosition}
                  aria-label={
                    step["aria-label"] ||
                    translations.accessibility.stepperCurrentStepAriaLabel({
                      currentStep: (stepPosition + 1).toString(),
                    })
                  }
                  className={clsx("relative flex w-full items-center", {
                    "pr-12": index !== stepArray.length - 1,
                  })}
                >
                  {hasOnStepChange ? (
                    <button
                      aria-current={status === "active" ? "step" : undefined}
                      className={clsx(
                        "group flex w-full flex-row items-center justify-center gap-x-3 rounded-large py-2.5",
                        "!outline-none-v3 focus-visible:!outline-focus-visible focus-visible:!outline-offset-4",
                        stepClassName,
                      )}
                      onClick={() => setCurrentStep(stepPosition)}
                    >
                      {stepContent}
                    </button>
                  ) : (
                    <div
                      aria-current={status === "active" ? "step" : undefined}
                      className={clsx(
                        "group flex w-full flex-row items-center justify-center gap-x-3 rounded-large py-2.5",
                        stepClassName,
                      )}
                    >
                      {stepContent}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </LazyMotion>
  );
}
